import TextEditor from 'components/text_editor/text_editor';
import Select from 'components/select/select';
import TeleconsultationWarning from 'components/teleconsultation_warning/teleconsultation';

import {
  STATUSES,
  CANCELLATION_REASONS,
  ADMIN_NEXT_ACTIONS,
  MANUAL_PAYMENT_STATUSES,
} from 'config/teleconsultation';
import { BLANK } from 'config/shared';

import useWindowDimensions, {
  isMobileResolution,
} from 'helpers/useWindowDimensions';

const Details = ({
  teleconsultation,
  doctorOptions,
  nurseOptions,
  handleDoctorSelect,
  handleNurseSelect,
  handleSelectChange,
  handleEditorChange,
}) => {
  const { width } = useWindowDimensions();
  const isMobile = isMobileResolution(width);

  if (!teleconsultation) return;

  const statusLabel = STATUSES.find((r) => r.value === teleconsultation.status);

  const cancellationLabel = CANCELLATION_REASONS.find(
    (r) => r.value === teleconsultation.cancellationReason,
  ) || {
    id: teleconsultation.cancellationReason,
    label: teleconsultation.cancellationReason,
  };

  const nextActionLabel = ADMIN_NEXT_ACTIONS.find(
    (r) => r.value === teleconsultation.nextAction,
  ) || {
    id: teleconsultation.nextAction,
    label: teleconsultation.nextAction,
  };

  const manualPaymentStatusLabel = MANUAL_PAYMENT_STATUSES.find(
    (r) => r.value === teleconsultation.manualPaymentStatus,
  );

  const doctorLabel =
    doctorOptions.find((d) => d.id === teleconsultation.doctor?.id) || BLANK;

  const nurseLabel =
    nurseOptions.find((d) => d.id === teleconsultation.nurse?.id) || BLANK;

  const DesktopView = (
    <section>
      <div className="pa5 pt4 br4 br--bottom-top bg-grouped-table flex flex-column">
        {teleconsultation.cancelled && (
          <TeleconsultationWarning
            cancelledBy={teleconsultation.cancelledBy}
            eventCancellationReason={teleconsultation.eventCancellationReason}
          />
        )}
        <div className="w-100 flex flex-column">
          <div className="w-100 flex flex-row items-start justify-between margin-between">
            <div className="w-50">
              <Select
                className="status-select"
                valid={true}
                isMulti={false}
                label="Status"
                value={statusLabel}
                options={STATUSES}
                onChange={(selectedOption) =>
                  handleSelectChange('status', selectedOption)
                }
              />
            </div>
            <div className="w-50">
              <Select
                className="next-select"
                valid={true}
                isMulti={false}
                withOther
                label="Next action"
                value={nextActionLabel}
                options={ADMIN_NEXT_ACTIONS}
                onChange={(selectedOption) =>
                  handleSelectChange('nextAction', selectedOption)
                }
              />
            </div>
          </div>
          <div className="w-100 flex flex-row justify-between margin-between">
            <div className="w-50">
              <Select
                className="paymentStatus-select"
                valid={true}
                isMulti={false}
                label="Manual payment status"
                value={manualPaymentStatusLabel}
                options={MANUAL_PAYMENT_STATUSES}
                onChange={(selectedOption) =>
                  handleSelectChange('manualPaymentStatus', selectedOption)
                }
              />
            </div>
            <div className="w-50">
              <Select
                className="cancellation-select"
                valid={true}
                isMulti={false}
                withOther
                label="Cancellation reason"
                value={cancellationLabel}
                options={CANCELLATION_REASONS}
                onChange={(selectedOption) =>
                  handleSelectChange('cancellationReason', selectedOption)
                }
              />
            </div>
          </div>
          <div className="w-100 flex flex-row justify-between margin-between">
            <div className="w-50">
              <Select
                className="doctor-select"
                valid={true}
                isMulti={false}
                label="Doctor"
                value={doctorLabel}
                options={doctorOptions}
                onChange={(selectedOption) =>
                  handleDoctorSelect(selectedOption)
                }
              />
            </div>
            <div className="w-50">
              <Select
                className="nurse-select"
                valid={true}
                isMulti={false}
                label="Nurse"
                value={nurseLabel}
                options={nurseOptions}
                onChange={(selectedOption) => handleNurseSelect(selectedOption)}
              />
            </div>
          </div>
          <div className="w-100 flex flex-row justify-between margin-between">
            <TextEditor
              name="doctorNotes"
              valid={true}
              value={teleconsultation.doctorNotes}
              label="Doctor notes"
              onChange={(data) => handleEditorChange('doctorNotes', data)}
            />
          </div>
          <div className="w-100 flex flex-row justify-between margin-between">
            <TextEditor
              name="adminNotes"
              valid={true}
              value={teleconsultation.adminNotes}
              label="Admin notes"
              onChange={(data) => handleEditorChange('adminNotes', data)}
            />
          </div>
        </div>
      </div>
    </section>
  );

  const MobileView = (
    <section>
      <div className="pa5 pt4 br4 br--bottom-top bg-grouped-table flex flex-column">
        <div className="w-100 flex flex-column">
          <div>
            <Select
              className="status-select"
              valid={true}
              isMulti={false}
              label="Status"
              value={statusLabel}
              options={STATUSES}
              onChange={(selectedOption) =>
                handleSelectChange('status', selectedOption)
              }
            />
          </div>
          <div>
            <Select
              className="next-select"
              valid={true}
              isMulti={false}
              withOther
              label="Next action"
              value={nextActionLabel}
              options={ADMIN_NEXT_ACTIONS}
              onChange={(selectedOption) =>
                handleSelectChange('nextAction', selectedOption)
              }
            />
          </div>
          <div>
            <Select
              className="paymentStatus-select"
              valid={true}
              isMulti={false}
              label="Manual payment status"
              value={manualPaymentStatusLabel}
              options={MANUAL_PAYMENT_STATUSES}
              onChange={(selectedOption) =>
                handleSelectChange('manualPaymentStatus', selectedOption)
              }
            />
          </div>
          <div>
            <Select
              className="cancellation-select"
              valid={true}
              isMulti={false}
              withOther
              label="Cancellation reason"
              value={cancellationLabel}
              options={CANCELLATION_REASONS}
              onChange={(selectedOption) =>
                handleSelectChange('cancellationReason', selectedOption)
              }
            />
          </div>
          <div>
            <Select
              className="doctor-select"
              valid={true}
              isMulti={false}
              label="Doctor"
              value={doctorLabel}
              options={doctorOptions}
              onChange={(selectedOption) => handleDoctorSelect(selectedOption)}
            />
          </div>
          <div>
            <Select
              className="nurse-select"
              valid={true}
              isMulti={false}
              label="Nurse"
              value={nurseLabel}
              options={nurseOptions}
              onChange={(selectedOption) => handleNurseSelect(selectedOption)}
            />
          </div>
          <div>
            <TextEditor
              name="doctorNotes"
              valid={true}
              value={teleconsultation.doctorNotes}
              label="Doctor notes"
              onChange={(data) => handleEditorChange('doctorNotes', data)}
            />
          </div>
          <div>
            <TextEditor
              name="adminNotes"
              valid={true}
              value={teleconsultation.adminNotes}
              label="Admin notes"
              onChange={(data) => handleEditorChange('adminNotes', data)}
            />
          </div>
        </div>
      </div>
    </section>
  );

  return isMobile ? MobileView : DesktopView;
};

export default Details;
